import { Link } from "react-router-dom";
import { FileText, Mail, ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

export default function Terms() {
  return (
    <div className="min-h-screen">
      <Navigation />

      <section className="relative pt-28 md:pt-32 pb-10 md:pb-12">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium mb-5">
              <FileText className="w-3.5 h-3.5" /> Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </div>
            <h1 data-testid="terms-title" className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-tight">
              Terms &amp; <em className="not-italic gradient-text font-light">conditions.</em>
            </h1>
            <p className="mt-5 text-muted-foreground text-pretty">
              These terms govern your use of the {SITE.website} website. By accessing or using the site you agree to be bound by them.
            </p>
          </div>
        </div>
      </section>

      <main className="pb-12 md:pb-16">
        <div className="container mx-auto">
          <article className="max-w-3xl mx-auto rounded-3xl border border-border bg-card/40 p-6 md:p-10">

            <Section title="1. About this website">
              <p>
                This website is owned and operated by <strong>{SITE.name}</strong>, a psychiatric mental-health practice based in {SITE.address}. References to &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo; mean {SITE.name}.
              </p>
            </Section>

            <Section title="2. No medical advice">
              <p>
                Information on this website is provided for general educational purposes only. It is <strong>not a substitute for professional medical advice, diagnosis, or treatment</strong>. Always seek the advice of a qualified clinician with any questions you may have regarding a medical or mental-health condition.
              </p>
              <p>
                <strong>If you are experiencing a medical emergency or are in crisis, call 911</strong> or go to your nearest emergency room. In the U.S. you can also call or text <strong>988</strong> to reach the Suicide & Crisis Lifeline.
              </p>
            </Section>

            <Section title="3. Appointments and scheduling">
              <p>
                Appointment requests submitted through this website (including the contact form and our scheduling tool) are not confirmed until you receive a written confirmation from us. Submitting a request does not create a clinician-patient relationship.
              </p>
              <p>
                Late cancellation, no-show, and other practice policies will be provided to you in writing before your first visit.
              </p>
            </Section>

            <Section title="4. Acceptable use">
              <p>You agree not to:</p>
              <ul>
                <li>Use this website for any unlawful purpose.</li>
                <li>Submit false, misleading, or harmful information.</li>
                <li>Attempt to gain unauthorized access to any part of the site or its underlying systems.</li>
                <li>Interfere with or disrupt the website or its servers.</li>
              </ul>
            </Section>

            <Section title="5. Intellectual property">
              <p>
                All content on this website — including text, graphics, logos, images, illustrations, and code — is the property of {SITE.name} or its licensors and is protected by copyright and other intellectual-property laws. You may not copy, reproduce, distribute, or create derivative works from any portion of the site without our written permission, except for personal, non-commercial reference use.
              </p>
            </Section>

            <Section title="6. Third-party links and tools">
              <p>
                This website may link to or embed third-party services such as Calendly. We are not responsible for the content, privacy practices, or availability of any third-party site or service. Your use of those services is governed by their own terms and privacy policies.
              </p>
            </Section>

            <Section title="7. Disclaimer of warranties">
              <p>
                This website is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis without warranties of any kind, express or implied. We do not warrant that the site will be uninterrupted, error-free, or free of harmful components. To the fullest extent permitted by law, we disclaim all warranties, including merchantability, fitness for a particular purpose, and non-infringement.
              </p>
            </Section>

            <Section title="8. Limitation of liability">
              <p>
                To the fullest extent permitted by applicable law, {SITE.name} and its providers shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your access to or use of this website, even if we have been advised of the possibility of such damages.
              </p>
            </Section>

            <Section title="9. Privacy">
              <p>
                Your use of this website is also governed by our <Link to="/privacy">Privacy Policy</Link>, which explains how we collect, use, and protect your information.
              </p>
            </Section>

            <Section title="10. Changes to these terms">
              <p>
                We may update these Terms &amp; Conditions from time to time. The &ldquo;Last updated&rdquo; date at the top reflects the most recent revision. Continued use of the site after changes are posted constitutes acceptance of the updated terms.
              </p>
            </Section>

            <Section title="11. Governing law">
              <p>
                These Terms are governed by the laws of the State of {SITE.state}, USA, without regard to its conflict-of-law principles. Any disputes shall be resolved in the state or federal courts located in {SITE.state}.
              </p>
            </Section>

            <Section title="12. Contact">
              <p>
                If you have any questions about these Terms, please contact:
              </p>
              <ul>
                <li>{SITE.name}</li>
                <li>{SITE.address}</li>
                <li>Email: <a href={`mailto:${SITE.email}`}>{SITE.email}</a></li>
                <li>Phone: <a href={`tel:${SITE.phoneRaw}`}>{SITE.phone}</a></li>
              </ul>
            </Section>

            <div className="mt-10 pt-6 border-t border-border/70 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Button asChild variant="outline" className="rounded-full border-border h-11 px-5">
                <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Back home</Link>
              </Button>
              <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-5">
                <a href={`mailto:${SITE.email}?subject=${encodeURIComponent("Terms question")}`}>
                  <Mail className="w-4 h-4 mr-2" /> Contact us
                </a>
              </Button>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8 last:mb-0">
      <h2 className="font-display text-xl md:text-2xl tracking-tight mb-3 text-foreground">{title}</h2>
      <div className="space-y-3 text-foreground/85 leading-relaxed [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-primary/80 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_strong]:text-foreground">
        {children}
      </div>
    </section>
  );
}
